import { useState } from 'react'
import { ShoppingCart, Ticket, Heart, Gift, Plus, Minus, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import { useAuthStore } from '@/store/useAuthStore'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { formatPrice, formatPriceWithCents } from '@/utils/format'
import { createFundraiserCheckout } from '@/services/stripe'
import type { FundraiserListing, FundraiserProduct, SponsorshipTier } from '@/types/listing'

interface FundraiserSectionProps {
  listing: FundraiserListing
}

export function FundraiserSection({ listing }: FundraiserSectionProps) {
  const fundraiserType = listing.fundraiserType

  return (
    <div className="space-y-6">
      {/* Progress bar */}
      {listing.targetAmount && listing.targetAmount > 0 && (
        <FundraiserProgress
          raised={listing.amountRaised || 0}
          target={listing.targetAmount}
          backers={listing.backerCount || 0}
        />
      )}

      {/* Type-specific content */}
      {fundraiserType === 'products' && listing.products && (
        <ProductsSection products={listing.products} listingId={listing.id} />
      )}

      {fundraiserType === 'raffle' && (
        <RaffleSection listing={listing} />
      )}

      {fundraiserType === 'sponsorship' && (
        <SponsorshipSection tiers={listing.tiers || []} listingId={listing.id} />
      )}

      {/* Auction fundraisers use BidSection (rendered above via isAuction check) */}
    </div>
  )
}

// Progress bar component
function FundraiserProgress({
  raised,
  target,
  backers,
}: {
  raised: number
  target: number
  backers: number
}) {
  const percentage = Math.min((raised / target) * 100, 100)

  return (
    <div className="card p-4 sm:p-6">
      <div className="flex items-end justify-between mb-2">
        <div>
          <p className="text-xl sm:text-2xl font-bold text-primary-600">{formatPrice(raised)}</p>
          <p className="text-sm text-slate-500">raised of {formatPrice(target)} goal</p>
        </div>
        <p className="text-sm text-slate-500">{backers} backers</p>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary-500 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-right text-sm text-slate-500 mt-1">{percentage.toFixed(0)}%</p>
    </div>
  )
}

// Products section
function ProductsSection({
  products,
  listingId,
}: {
  products: FundraiserProduct[]
  listingId: string
}) {
  const { isAuthenticated } = useAuthStore()
  const { openModal, showToast } = useUIStore()
  const [cart, setCart] = useState<Record<string, number>>({})
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [reference, setReference] = useState('')

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) => {
      const current = prev[productId] || 0
      const newQty = Math.max(0, current + delta)
      if (newQty === 0) {
        const { [productId]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [productId]: newQty }
    })
  }

  const cartTotal = products.reduce((sum, product) => {
    return sum + (cart[product.id] || 0) * product.price
  }, 0)

  const cartCount = Object.values(cart).reduce((sum, qty) => sum + qty, 0)

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }

    setIsCheckingOut(true)
    try {
      const items = Object.entries(cart)
        .filter(([, qty]) => qty > 0)
        .map(([productId, quantity]) => ({
          type: 'product' as const,
          itemId: productId,
          quantity,
        }))

      const result = await createFundraiserCheckout({ listingId, items, reference: reference.trim() || undefined })
      window.location.href = result.url
    } catch (error) {
      console.error('Fundraiser product checkout failed:', error)
      showToast(error instanceof Error ? error.message : 'Checkout failed. Please try again.', 'error')
      setIsCheckingOut(false)
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <ShoppingCart className="w-5 h-5" />
        Products
      </h3>

      <div className="grid gap-4">
        {products.map((product) => {
          const quantity = cart[product.id] || 0
          const inStock = product.stock === undefined || product.stock > (product.sold || 0)

          return (
            <div key={product.id} className="card p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4">
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full sm:w-20 h-40 sm:h-20 rounded-lg object-cover"
                />
              ) : (
                <div className="w-full sm:w-20 h-32 sm:h-20 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Gift className="w-8 h-8 text-slate-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium">{product.name}</h4>
                {product.description && (
                  <p className="text-sm text-slate-500 line-clamp-2">{product.description}</p>
                )}
                <p className="text-primary-600 font-semibold mt-1">
                  {formatPriceWithCents(product.price)}
                </p>
              </div>
              <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-between">
                {inStock ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(product.id, -1)}
                      className="p-1 rounded-lg hover:bg-slate-100"
                      disabled={quantity === 0}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-medium">{quantity}</span>
                    <button
                      onClick={() => updateQuantity(product.id, 1)}
                      className="p-1 rounded-lg hover:bg-slate-100"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-slate-400">Sold out</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Cart summary */}
      {cartCount > 0 && (
        <div className="card p-3 sm:p-4 bg-primary-50 border-primary-100">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium">{cartCount} items</span>
            <span className="text-lg font-bold">{formatPriceWithCents(cartTotal)}</span>
          </div>
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <label htmlFor="product-reference" className="block text-sm font-medium text-slate-800 mb-1">
              Your Name / Reference
            </label>
            <input
              id="product-reference"
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. Student name, class, or family"
              className="input w-full"
              disabled={isCheckingOut}
            />
            <p className="text-xs text-amber-700 mt-1">Helps the organiser identify your order for collection</p>
          </div>
          <Button onClick={handleCheckout} className="w-full" disabled={isCheckingOut}>
            {isCheckingOut ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Complete Purchase'
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

// Raffle section
function RaffleSection({ listing }: { listing: FundraiserListing }) {
  const { isAuthenticated } = useAuthStore()
  const { openModal, showToast } = useUIStore()
  const [ticketCount, setTicketCount] = useState(1)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [reference, setReference] = useState('')

  const ticketPrice = listing.ticketPrice || 0
  const ticketsSold = listing.ticketsSold || 0
  const totalTickets = listing.totalTickets || 0
  const ticketsRemaining = totalTickets - ticketsSold

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }

    setIsPurchasing(true)
    try {
      const result = await createFundraiserCheckout({
        listingId: listing.id,
        items: [{ type: 'raffle', itemId: 'ticket', quantity: ticketCount }],
        reference: reference.trim() || undefined,
      })
      window.location.href = result.url
    } catch (error) {
      console.error('Raffle ticket purchase failed:', error)
      showToast(error instanceof Error ? error.message : 'Purchase failed. Please try again.', 'error')
      setIsPurchasing(false)
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Ticket className="w-5 h-5" />
        Raffle Tickets
      </h3>

      {/* Prizes */}
      {listing.prizes && listing.prizes.length > 0 && (
        <div className="card p-3 sm:p-4">
          <h4 className="font-medium mb-3">Prizes</h4>
          <div className="space-y-3">
            {listing.prizes.map((prize, index) => (
              <div key={prize.id} className="flex gap-3">
                <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-accent-100 text-accent-600 text-sm font-medium flex items-center justify-center">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{prize.name}</p>
                  {prize.description && (
                    <p className="text-sm text-slate-600 mt-0.5">{prize.description}</p>
                  )}
                  {prize.value && (
                    <p className="text-sm text-slate-500 mt-0.5">Value: {formatPriceWithCents(prize.value)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ticket purchase */}
      <div className="card p-3 sm:p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-medium">Ticket Price</p>
            <p className="text-xl sm:text-2xl font-bold text-primary-600">{formatPriceWithCents(ticketPrice)}</p>
          </div>
          {totalTickets > 0 && (
            <div className="text-right">
              <p className="text-sm text-slate-500">Available</p>
              <p className="font-medium">
                {ticketsSold === 0
                  ? `${totalTickets} tickets`
                  : `${ticketsRemaining} of ${totalTickets}`}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 mb-4">
          <label className="text-sm font-medium">Quantity:</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTicketCount(Math.max(1, ticketCount - 1))}
              className="p-2 rounded-lg hover:bg-slate-100"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-12 text-center font-medium text-lg">{ticketCount}</span>
            <button
              onClick={() => setTicketCount(ticketCount + 1)}
              className="p-2 rounded-lg hover:bg-slate-100"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4 py-3 border-t border-slate-200">
          <span className="font-medium">Total</span>
          <span className="text-lg sm:text-xl font-bold">{formatPriceWithCents(ticketPrice * ticketCount)}</span>
        </div>

        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <label htmlFor="raffle-reference" className="block text-sm font-medium text-slate-800 mb-1">
            Your Name / Reference
          </label>
          <input
            id="raffle-reference"
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. Student name, class, or family"
            className="input w-full"
            disabled={isPurchasing}
          />
          <p className="text-xs text-amber-700 mt-1">Helps the organiser identify your order for collection</p>
        </div>

        <Button onClick={handlePurchase} className="w-full" disabled={isPurchasing}>
          {isPurchasing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Ticket className="w-4 h-4 mr-2" />
              Buy {ticketCount} Ticket{ticketCount > 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>

      {/* Terms & Conditions */}
      {listing.termsAndConditions && (
        <div className="card p-3 sm:p-4">
          <h4 className="font-medium mb-2">Terms & Conditions</h4>
          <p className="text-sm text-slate-600 whitespace-pre-line">{listing.termsAndConditions}</p>
        </div>
      )}
    </div>
  )
}

// Sponsorship section
function SponsorshipSection({
  tiers,
  listingId,
}: {
  tiers: SponsorshipTier[]
  listingId: string
}) {
  const { isAuthenticated } = useAuthStore()
  const { openModal, showToast } = useUIStore()
  const [selectedTier, setSelectedTier] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [customAmount, setCustomAmount] = useState<string>('')
  const [reference, setReference] = useState('')

  const handleSponsor = async (tierId: string) => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }

    setSelectedTier(tierId)
    setIsProcessing(true)
    try {
      const result = await createFundraiserCheckout({
        listingId,
        items: [{ type: 'sponsorship', itemId: tierId, quantity: 1 }],
        reference: reference.trim() || undefined,
      })
      window.location.href = result.url
    } catch (error) {
      console.error('Sponsorship purchase failed:', error)
      showToast(error instanceof Error ? error.message : 'Purchase failed. Please try again.', 'error')
      setSelectedTier(null)
      setIsProcessing(false)
    }
  }

  const handleCustomSponsor = async () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }

    const amount = parseFloat(customAmount)
    if (!Number.isFinite(amount) || amount < 1) {
      showToast('Enter a sponsorship amount of at least $1', 'error')
      return
    }

    setSelectedTier('custom')
    setIsProcessing(true)
    try {
      const result = await createFundraiserCheckout({
        listingId,
        items: [{ type: 'sponsorship', itemId: 'custom', quantity: 1, customAmount: amount }],
        reference: reference.trim() || undefined,
      })
      window.location.href = result.url
    } catch (error) {
      console.error('Custom sponsorship purchase failed:', error)
      showToast(error instanceof Error ? error.message : 'Purchase failed. Please try again.', 'error')
      setSelectedTier(null)
      setIsProcessing(false)
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Heart className="w-5 h-5" />
        Sponsorship Tiers
      </h3>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <label htmlFor="sponsor-reference" className="block text-sm font-medium text-slate-800 mb-1">
          Your Name / Reference
        </label>
        <input
          id="sponsor-reference"
          type="text"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="e.g. Student name, class, or family"
          className="input w-full"
          disabled={isProcessing}
        />
        <p className="text-xs text-amber-700 mt-1">Helps the organiser identify your order for collection</p>
      </div>

      <div className="grid gap-4">
        {tiers.map((tier) => {
          const available = tier.limit === undefined || (tier.claimed || 0) < tier.limit
          const isSelected = selectedTier === tier.id

          return (
            <div
              key={tier.id}
              className={clsx(
                'card p-3 sm:p-4 transition-all',
                available ? 'hover:shadow-md' : 'opacity-60'
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <h4 className="font-semibold">{tier.name}</h4>
                  <p className="text-xl sm:text-2xl font-bold text-primary-600">
                    {formatPriceWithCents(tier.amount)}
                  </p>
                </div>
                {tier.limit && (
                  <span className="text-xs sm:text-sm text-slate-500 flex-shrink-0">
                    {tier.limit - (tier.claimed || 0)} left
                  </span>
                )}
              </div>

              <p className="text-sm sm:text-base text-slate-600 mb-3">{tier.description}</p>

              {tier.benefits.length > 0 && (
                <ul className="space-y-1 mb-4">
                  {tier.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-primary-500 mt-0.5">✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              )}

              <Button
                variant={available ? 'primary' : 'secondary'}
                onClick={() => handleSponsor(tier.id)}
                className="w-full"
                disabled={!available || isProcessing}
              >
                {isProcessing && isSelected ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : available ? (
                  'Select This Tier'
                ) : (
                  'Sold Out'
                )}
              </Button>
            </div>
          )
        })}

        {/* Custom sponsorship amount */}
        <div className="card p-3 sm:p-4 transition-all hover:shadow-md">
          <div className="mb-2">
            <h4 className="font-semibold">Choose Your Own Amount</h4>
            <p className="text-sm text-slate-600 mt-1">
              Prefer to sponsor with a different amount? Enter what you'd like to give.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <input
                type="number"
                min="1"
                step="1"
                inputMode="decimal"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="Amount in NZD"
                className="input pl-7 w-full"
                disabled={isProcessing}
                aria-label="Custom sponsorship amount"
              />
            </div>
            <Button
              variant="primary"
              onClick={handleCustomSponsor}
              disabled={isProcessing || !customAmount}
              className="sm:w-auto"
            >
              {isProcessing && selectedTier === 'custom' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Sponsor'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
